import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import VideoRow from '../components/VideoRow';
import VideoPlayer from '../components/VideoPlayer';
import { VideoPageAd, InVideoAd, VideoFloatAd, MobileAdVideo } from '../components/AdBanner';
import ShareButtons from '../components/ShareButtons';
import { 
  Heart, BookmarkPlus, Share2, ExternalLink, 
  ChevronLeft, Eye, ThumbsUp, Tag 
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Video = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeader } = useAuth();
  
  const [video, setVideo] = useState(location.state?.video || null);
  const [relatedVideos, setRelatedVideos] = useState([]);
  const [loading, setLoading] = useState(!location.state?.video);
  const [inWatchlist, setInWatchlist] = useState(false);
  const [inFavorites, setInFavorites] = useState(false);

  useEffect(() => {
    if (!video) {
      fetchVideoDetails();
    }
    fetchRelatedVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (video) {
      trackWatchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video?.id, isAuthenticated]);

  const fetchVideoDetails = async () => {
    try {
      const response = await axios.get(`${API}/content/video/${id}`);
      setVideo(response.data);
    } catch (error) {
      console.error('Error fetching video:', error);
      toast.error('Could not load video');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedVideos = async () => {
    try {
      const response = await axios.get(`${API}/content/videos?page=1`);
      setRelatedVideos(response.data.videos?.slice(0, 12) || []);
    } catch (error) {
      console.error('Error fetching related videos:', error);
    }
  };

  const trackWatchHistory = async () => {
    if (isAuthenticated && video) {
      try {
        await axios.post(`${API}/user/history`, {
          video_id: video.id || id,
          title: video.title,
          thumbnail: video.thumbnail,
          duration: video.duration,
          progress: 0,
          watched_at: new Date().toISOString()
        }, getAuthHeader());
      } catch (error) {
        console.log('Could not track watch history');
      }
    }
  };

  const trackProgress = async (progress) => {
    if (isAuthenticated && video) {
      try {
        await axios.post(`${API}/user/history`, {
          video_id: video.id || id,
          title: video.title,
          thumbnail: video.thumbnail,
          duration: video.duration,
          progress: progress,
          watched_at: new Date().toISOString()
        }, getAuthHeader());
      } catch (error) {
        // Non-critical: silently fail but log for debugging
        console.debug('Track progress failed:', error.message);
      }
    }
  };

  const handleAddToWatchlist = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to add to watchlist');
      navigate('/login');
      return;
    }
    try {
      await axios.post(`${API}/user/watchlist`, {
        video_id: video.id || id,
        title: video.title,
        thumbnail: video.thumbnail,
        duration: video.duration,
        added_at: new Date().toISOString()
      }, getAuthHeader());
      setInWatchlist(true);
      toast.success('Added to watchlist');
    } catch (err) {
      if (err.response?.status === 400) {
        toast.info('Already in watchlist');
        setInWatchlist(true);
      } else {
        toast.error('Failed to add to watchlist');
      }
    }
  };

  const handleAddToFavorites = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to add to favorites');
      navigate('/login');
      return;
    }
    try {
      await axios.post(`${API}/user/favorites`, {
        video_id: video.id || id,
        title: video.title,
        thumbnail: video.thumbnail,
        duration: video.duration,
        added_at: new Date().toISOString()
      }, getAuthHeader());
      setInFavorites(true);
      toast.success('Added to favorites');
    } catch (err) {
      if (err.response?.status === 400) {
        toast.info('Already in favorites');
        setInFavorites(true);
      } else {
        toast.error('Failed to add to favorites');
      }
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: video?.title,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 px-4 md:px-8 lg:px-16" data-testid="video-loading">
        <div className="aspect-video skeleton mb-6 max-w-5xl" />
        <div className="h-8 skeleton w-1/2 mb-4" />
        <div className="h-4 skeleton w-1/4" />
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen pt-20 px-4 flex flex-col items-center justify-center" data-testid="video-not-found">
        <h2 className="text-2xl text-white mb-4">Video not found</h2>
        <Button onClick={() => navigate('/')}>Go Home</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-[#050505]" data-testid="video-page">
      <SEO 
        title={video.title} 
        description={`Watch ${video.title} on VelvetStream. ${video.views || ''} views. Browse related videos and 150+ categories.`}
        path={`/video/${id}`}
        type="video.other"
        image={video.thumbnail}
      />
      <div className="px-4 md:px-8 lg:px-16 py-8">
        {/* Back button */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-5 h-5" />
          Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2">
            {/* Video Player */}
            <div className="mb-6">
              <VideoPlayer
                videoUrl={video.video_url || video.url}
                embedUrl={video.embed}
                thumbnail={video.thumbnail || 'https://images.unsplash.com/photo-1721040530907-646692053af8?w=1280'}
                title={video.title}
                onProgress={(progress) => {
                  // Track progress for continue watching
                  if (isAuthenticated && progress > 0) {
                    trackProgress(progress);
                  }
                }}
              />
            </div>

            {/* Video Info */}
            <h1 
              className="text-3xl md:text-4xl font-normal tracking-wide uppercase mb-4 text-white"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              data-testid="video-title"
            >
              {video.title}
            </h1>

            {/* Stats */}
            <div className="flex flex-wrap items-center gap-4 text-gray-400 text-sm mb-6">
              <span className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                {video.views} views
              </span>
              {video.rating && (
                <span className="flex items-center gap-1 text-green-500">
                  <ThumbsUp className="w-4 h-4" />
                  {video.rating}%
                </span>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-3 mb-8">
              <Button 
                onClick={handleAddToFavorites}
                variant={inFavorites ? 'default' : 'outline'}
                className={inFavorites 
                  ? 'bg-white text-black' 
                  : 'border-white/30 bg-transparent text-white hover:bg-white/10'
                }
                data-testid="video-favorite-btn"
              >
                <Heart className={`w-4 h-4 mr-2 ${inFavorites ? 'fill-black' : ''}`} />
                {inFavorites ? 'Favorited' : 'Favorite'}
              </Button>
              
              <Button 
                onClick={handleAddToWatchlist}
                variant={inWatchlist ? 'default' : 'outline'}
                className={inWatchlist 
                  ? 'bg-white text-black' 
                  : 'border-white/30 bg-transparent text-white hover:bg-white/10'
                }
                data-testid="video-watchlist-btn"
              >
                <BookmarkPlus className="w-4 h-4 mr-2" />
                {inWatchlist ? 'In Watchlist' : 'Watchlist'}
              </Button>
              
              {/* Social Share Buttons */}
              <ShareButtons 
                title={video.title} 
                url={`https://velvetstreams.xyz/video/${video.id}`} 
              />

              {video.url && video.url !== '#' && (
                <a 
                  href={video.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button 
                    variant="outline"
                    className="border-white/30 bg-transparent text-white hover:bg-white/10"
                    data-testid="video-external-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Watch on Source
                  </Button>
                </a>
              )}
            </div>

            {/* Tags */}
            {video.tags && video.tags.length > 0 && (
              <div className="mb-8">
                <h3 className="text-sm text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {video.tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-[#1A1A1A] text-gray-300 text-sm rounded-sm hover:bg-white/10 cursor-pointer transition-colors"
                    >
                      {tag.tag_name || tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Ad below video info - In-Video Ad Zone 1115338 */}
            <div className="mb-8">
              <InVideoAd />
            </div>

            {/* Mobile Ad */}
            <MobileAdVideo />
          </div>

          {/* Sidebar - Related Videos */}
          <div className="lg:col-span-1">
            {/* Sidebar Ad - Zone 1115344 */}
            <div className="mb-6">
              <VideoPageAd />
            </div>

            <h3 
              className="text-xl font-normal tracking-wide uppercase mb-4"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            >
              More Videos
            </h3>
            <div className="space-y-4">
              {relatedVideos.slice(0, 4).map((relVideo, index) => (
                <div 
                  key={relVideo.id || index}
                  onClick={() => navigate(`/video/${relVideo.id}`, { state: { video: relVideo } })}
                  className="flex gap-3 cursor-pointer group"
                  data-testid={`related-video-${index}`}
                >
                  <div className="w-40 flex-shrink-0 aspect-video bg-[#121212] rounded overflow-hidden">
                    <img 
                      src={relVideo.thumbnail || 'https://images.unsplash.com/photo-1721040530907-646692053af8?w=300'}
                      alt={relVideo.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm text-white line-clamp-2 group-hover:text-gray-300 transition-colors">
                      {relVideo.title}
                    </h4>
                    <p className="text-xs text-gray-500 mt-1">{relVideo.views} views</p>
                    <p className="text-xs text-gray-600">{relVideo.duration}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Related videos end */}
          </div>
        </div>

        {/* Related Videos Row */}
        <div className="mt-12">
          <VideoRow 
            title="You May Also Like" 
            videos={relatedVideos}
          />
        </div>

        {/* Float Ad - Zone 1115342 */}
        <VideoFloatAd />
      </div>
    </div>
  );
};

export default Video;
